import { Capacitor } from "@capacitor/core";
import { styled } from "@mui/material/styles";
import { bbox, FeatureCollection } from "@turf/turf";
import { getMapStateForPath } from "app/routes";
import { AnalyticsEvents, mixpanel } from "common/analytics";
import {
  MutableRefObject,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

// Cache the native platform check at module load time
// On native Capacitor platforms (iOS/Android), always treat as mobile regardless of viewport width
const IS_NATIVE_PLATFORM = Capacitor.isNativePlatform();

import { WebMercatorViewport } from "@math.gl/web-mercator";

import type { MapLayerMouseEvent } from "mapbox-gl";
import { Map, MapRef, useMap, ViewStateChangeEvent } from "react-map-gl/mapbox";

import AddIcon from "@mui/icons-material/Add";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RemoveIcon from "@mui/icons-material/Remove";
import { IconButton, Tooltip } from "@mui/material";
import { colorToDM, useHtmlElementSize, useIsMobile } from "common/utils";
import mapLogger from "common/utils/debugLogger";
import TerraDrawMapEditor from "features/mapDrawTools/TerraDrawMapEditor";
import {
  ArableFieldGeoJSONLayers,
  FieldGeoJSONLayers,
  FieldTileLayers,
  MapCursorTooltip,
} from "features/mapLayers";
import "mapbox-gl/dist/mapbox-gl.css";
import {
  getArableFieldsWithNoTilesFromArray,
  getFieldsWithNoTilesFromArray,
} from "model/fieldHelpers";
import { useArableFields } from "model/useArableFieldQueries";
import { useFields } from "model/useFieldQueries";
import { flushSync } from "react-dom";
import { useAppStore, useUIStore } from "stores";

interface ViewportState {
  latitude: number;
  longitude: number;
  zoom: number;
  bearing: number;
  pitch: number;
  width: number;
  height: number;
}

const initialViewport: ViewportState = {
  latitude: 52.794201,
  longitude: -1.1945036,
  zoom: 12,
  bearing: 0,
  pitch: 0,
  width: 0,
  height: 0,
};

type ViewportAction =
  | { type: "update"; payload: Partial<ViewportState> }
  | {
      type: "zoom-to-bounds";
      payload: { geom: FeatureCollection; animate: boolean };
    };

const viewportReducer = (
  state: ViewportState,
  action: ViewportAction
): ViewportState => {
  switch (action.type) {
    case "update":
      return { ...state, ...action.payload };
    case "zoom-to-bounds":
      if (!state.width || !state.height) {
        return state;
      } else if (state.width * state.height === 0) {
        return state;
      } else if (action.payload.geom.features.length === 0) {
        return state;
      } else {
        const viewportWidth = state.width ?? 100;
        const viewportHeight = state.height ?? 100;

        // Validate viewport dimensions
        if (
          viewportWidth <= 0 ||
          viewportHeight <= 0 ||
          !isFinite(viewportWidth) ||
          !isFinite(viewportHeight)
        ) {
          return state;
        }

        const bounds = bbox(action.payload.geom);
        const minLng = bounds[0];
        const minLat = bounds[1];
        const maxLng = bounds[2];
        const maxLat = bounds[3];

        // Validate bounding box coordinates
        if (
          !Number.isFinite(minLng) ||
          !Number.isFinite(minLat) ||
          !Number.isFinite(maxLng) ||
          !Number.isFinite(maxLat) ||
          minLng >= maxLng ||
          minLat >= maxLat
        ) {
          // Invalid bounding box - return current state without updating
          return state;
        }

        // Calculate padding values and ensure they don't exceed viewport dimensions
        // On native platforms (iOS/Android), always use mobile padding regardless of viewport width
        // This ensures iPad gets mobile treatment like iPhone
        const isMobileViewport = IS_NATIVE_PLATFORM || window.innerWidth < 600;
        // On mobile: account for UI overlays (top bar, bottom sheet)
        // On desktop: map container is already the visible area, so use minimal padding
        const leftPadding = isMobileViewport ? 30 : 50;
        const rightPadding = isMobileViewport ? 30 : 50;
        const topPadding = isMobileViewport ? 80 : 50;
        const bottomPadding = isMobileViewport
          ? Math.round(viewportHeight * 0.35)
          : 50;

        // Ensure padding doesn't exceed viewport dimensions
        const totalHorizontalPadding = leftPadding + rightPadding;
        const totalVerticalPadding = topPadding + bottomPadding;

        if (
          totalHorizontalPadding >= viewportWidth ||
          totalVerticalPadding >= viewportHeight
        ) {
          // Padding too large - use smaller padding or return state
          return state;
        }

        try {
          const newViewport = new WebMercatorViewport({
            ...state,
            width: viewportWidth,
            height: viewportHeight,
          });

          const { longitude, latitude, zoom } = newViewport.fitBounds(
            [
              [minLng, minLat] as [number, number],
              [maxLng, maxLat] as [number, number],
            ],
            {
              padding: {
                top: topPadding,
                left: leftPadding,
                right: rightPadding,
                bottom: bottomPadding,
              },
            }
          );

          // Validate the returned values
          if (!isFinite(longitude) || !isFinite(latitude) || !isFinite(zoom)) {
            return state;
          }

          return {
            ...state,
            longitude: longitude,
            latitude: latitude,
            zoom: zoom,
          };
        } catch (error) {
          // If fitBounds throws an error (e.g., assertion failure), return current state
          console.warn("Error fitting bounds to viewport:", error);
          return state;
        }
      }
    default:
      return state;
  }
};

interface MapViewProps {
  mapRef: MutableRefObject<MapRef | null>;
}

export const MapView = ({ mapRef }: MapViewProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const mapContainer = useRef<HTMLDivElement | null>(null);
  const [mapColorDM, setMapColorDM] = useState<string>("");
  const [hoveredFieldId, setHoveredFieldId] = useState<string>();
  const [tooltipVisible, setTooltipVisible] = useState<boolean>(false);
  const [tooltipOverMarker, setTooltipOverMarker] = useState<boolean>(false);
  const [tilesReady, setTilesReady] = useState<boolean>(false);
  const [mapKey, setMapKey] = useState(0);
  const [viewport, viewportDispatch] = useReducer(
    viewportReducer,
    initialViewport
  );
  const [height, width] = useHtmlElementSize(mapContainer.current);
  // Track if initial map resize is complete - zoom should wait for this
  const [isMapResizeComplete, setIsMapResizeComplete] = useState(false);
  // Use Zustand for current farm ID
  const farmid = useAppStore((state) => state.currentFarmId);

  // Get map state from centralized route config
  const mapState = useMemo(
    () => getMapStateForPath(location.pathname),
    [location.pathname]
  );
  const {
    showMap,
    isFieldEditMode,
    disableMapClick,
    selectedFieldId,
    editedFieldId,
  } = mapState;

  const questionnaireStarted = useUIStore(
    (state) => state.questionnaireStarted
  );
  const quickAssessmentStarted = useUIStore(
    (state) => state.quickAssessmentStarted
  );

  // Use Zustand for zoomToGeom
  const zoomToGeom = useUIStore((state) => state.zoomToGeom);
  const clearZoomToGeom = useUIStore((state) => state.clearZoomToGeom);
  // Use Zustand for isDraggingAnimalMarker
  const isDraggingAnimalMarker = useUIStore(
    (state) => state.isDraggingAnimalMarker
  );
  // Use Zustand for openDrawer
  const openDrawer = useUIStore((state) => state.openDrawer);
  // Use Zustand for auth state
  const loggedIn = useAppStore((state) => state.isAuthenticated);
  // Use Zustand for geolocation
  const postcodeLocation = useUIStore((state) => state.postcodeLocation);
  const navigatorLocation = useUIStore((state) => state.navigatorLocation);
  // Derive fields with no tiles from useFields
  const { data: fields = [] } = useFields();
  const fieldsWithNoTiles = useMemo(
    () => getFieldsWithNoTilesFromArray(fields),
    [fields]
  );
  // Derive arable fields with no tiles from useArableFields
  const { data: arableFields = [] } = useArableFields();
  const arableFieldsWithNoTiles = useMemo(
    () => getArableFieldsWithNoTilesFromArray(arableFields),
    [arableFields]
  );
  const interactiveLayerIds = ["field-fill", "arable-field-fill"];
  const isMobile = useIsMobile();
  const maxZoom = isMobile ? 19 : 17;

  // Handler to update tiles ready state from FieldTileLayers
  // Tiles are "ready" when they've finished loading and have data to display
  const handleTilesReadyChange = useCallback((ready: boolean) => {
    setTilesReady(ready);
  }, []);
  const user = useAppStore((state) => state.user);
  const isCorporateUser =
    user?.corporations?.length && user.corporations.length > 0;
  // We do not want to fly to the geometry the first time,
  // otherwise the map will request tiles on all zoom levels.
  const initialized = useRef(false);

  const isSyncing = useRef(false);

  useEffect(() => {
    viewportDispatch({
      type: "update",
      payload: { height: height, width: width },
    });
  }, [height, width]);

  // ResizeObserver to watch container size changes (handles layout shifts, UI overlays, etc.)
  useEffect(() => {
    if (!mapContainer.current) return;

    const resizeMap = () => {
      const map = mapRef.current?.getMap();
      if (map) {
        // Use requestAnimationFrame to ensure DOM has updated
        requestAnimationFrame(() => {
          map.resize();
          mapLogger.debug("Map resized via ResizeObserver", {
            width: mapContainer.current?.offsetWidth,
            height: mapContainer.current?.offsetHeight,
          });
        });
      }
    };

    // Create ResizeObserver to watch the container element directly
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width: newWidth, height: newHeight } = entry.contentRect;
        // Only resize if dimensions are valid
        if (newWidth > 0 && newHeight > 0) {
          resizeMap();
        }
      }
    });

    resizeObserver.observe(mapContainer.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [mapRef]);

  // Force map resize on window resize (fallback for older browsers)
  useEffect(() => {
    const handleResize = () => {
      const map = mapRef.current?.getMap();
      if (map) {
        // Use requestAnimationFrame instead of setTimeout for better timing
        requestAnimationFrame(() => {
          map.resize();
        });
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [mapRef]);

  // Ensure map is resized when dimensions first become available
  // IMPORTANT: zoomToGeom effect depends on isMapResizeComplete to avoid race conditions
  useEffect(() => {
    if (width > 0 && height > 0) {
      const map = mapRef.current?.getMap();
      if (map) {
        // Small delay to ensure map is fully initialized
        requestAnimationFrame(() => {
          map.resize();
          mapLogger.debug("Map resized when dimensions became available", {
            width,
            height,
          });
          // Signal that the map resize is complete - zoom can now proceed
          setIsMapResizeComplete(true);
        });
      } else {
        // No map yet, but dimensions are available - mark resize as complete
        // so zoom can proceed when map becomes available
        setIsMapResizeComplete(true);
      }
    }
  }, [mapRef, width, height]);

  // Handle WebGL context loss - remount the map to recover
  const handleMapLoad = useCallback(() => {
    const map = mapRef.current?.getMap();
    if (!map) {
      mapLogger.warn("handleMapLoad called but map ref is null");
      return;
    }

    // Adjust scroll/trackpad zoom rate as per MapLibre ScrollZoomHandler docs:
    // https://maplibre.org/maplibre-gl-js/docs/API/classes/ScrollZoomHandler/#setZoomRate
    // Also try to cover any touch/trackpad zoom handler the Map instance exposes.
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const anyMap = map as any;
      const scrollZoom = anyMap.scrollZoom;
      if (scrollZoom) {
        // Prefer official APIs when available
        if (typeof scrollZoom.setZoomRate === "function") {
          scrollZoom.setZoomRate(1 / 100);
        }
        if (typeof scrollZoom.setWheelZoomRate === "function") {
          scrollZoom.setWheelZoomRate(1 / 100);
        }
        // Fallback for older MapLibre versions that expose internal rates
        if ("zoomRate" in scrollZoom) {
          scrollZoom.zoomRate = 1 / 100;
        }
        if ("wheelZoomRate" in scrollZoom) {
          scrollZoom.wheelZoomRate = 1 / 100;
        }
      }

      const touchZoomRotate = anyMap.touchZoomRotate;
      if (touchZoomRotate) {
        if (typeof touchZoomRotate.setZoomRate === "function") {
          touchZoomRotate.setZoomRate(1 / 100);
        }
        if ("zoomRate" in touchZoomRotate) {
          touchZoomRotate.zoomRate = 1 / 100;
        }
      }
    } catch (error) {
      mapLogger.warn("Failed to set scroll zoom rate", { error });
    }

    mapLogger.info("Map loaded, setting up WebGL context loss handlers", {
      mapKey,
    });

    const handleError = (e: { error?: { message?: string } }) => {
      const errorMessage = e.error?.message ?? "";
      mapLogger.debug("Map error event received", { errorMessage });
      if (
        errorMessage.includes("WebGL") ||
        errorMessage.includes("context lost")
      ) {
        mapLogger.error("WebGL context lost via error event - remounting map", {
          errorMessage,
        });
        setTimeout(() => setMapKey((prev) => prev + 1), 500);
      }
    };

    map.on("error", handleError);

    // Listen directly on the canvas for context loss
    const canvas = map.getCanvas();
    mapLogger.debug("Canvas element obtained for context loss listener", {
      canvasWidth: canvas.width,
      canvasHeight: canvas.height,
    });

    const handleContextLost = (e: Event) => {
      e.preventDefault();
      mapLogger.error("WebGL context lost event fired - remounting map", {
        eventType: e.type,
      });
      // Delay slightly to allow any pending operations to complete
      setTimeout(() => setMapKey((prev) => prev + 1), 500);
    };

    canvas.addEventListener("webglcontextlost", handleContextLost);
    mapLogger.info("WebGL context loss listener attached to canvas");

    // On mobile, set padding to account for UI overlays (top bar, bottom sheet)
    // On desktop, the map container is already sized to the visible area, so no padding needed
    // On native platforms (iOS/Android), always use mobile padding regardless of viewport width
    const isMobileViewport = IS_NATIVE_PLATFORM || window.innerWidth < 600;
    if (isMobileViewport) {
      map.setPadding({
        left: 30,
        right: 30,
        top: 80, // Account for farm switch at top
        bottom: Math.round(window.innerHeight * 0.35), // Account for bottom sheet
      });
      mapLogger.info("Mobile map padding set for UI overlays");
    }

    // Ensure map is properly sized on initial load
    // This handles cases where the container has dimensions before the map initializes
    requestAnimationFrame(() => {
      map.resize();
      mapLogger.debug("Map resized on initial load");
    });
  }, [mapRef, mapKey]);

  // Update mobile map padding when viewport size changes
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;

    // On native platforms (iOS/Android), always use mobile padding regardless of viewport width
    const isMobileViewport = IS_NATIVE_PLATFORM || window.innerWidth < 600;
    if (isMobileViewport) {
      map.setPadding({
        left: 30,
        right: 30,
        top: 80,
        bottom: Math.round(window.innerHeight * 0.35),
      });
    } else {
      // Desktop: no padding needed since map container is already the visible area
      map.setPadding({ left: 0, right: 0, top: 0, bottom: 0 });
    }
  }, [mapRef, width, height]);

  // Note: Farm view zoom is handled by FarmView and FarmOverviewContent components

  useEffect(() => {
    // Wait for map resize to complete before processing zoom
    // This prevents a race condition where zoom happens before the map is properly sized
    if (!isMapResizeComplete) {
      return;
    }

    if (zoomToGeom && zoomToGeom.features.length > 0) {
      const bounds = bbox(zoomToGeom);
      const minLng = bounds[0];
      const minLat = bounds[1];
      const maxLng = bounds[2];
      const maxLat = bounds[3];

      // Validate bounding box
      if (
        !Number.isFinite(minLng) ||
        !Number.isFinite(minLat) ||
        !Number.isFinite(maxLng) ||
        !Number.isFinite(maxLat) ||
        minLng >= maxLng ||
        minLat >= maxLat
      ) {
        clearZoomToGeom();
        return;
      }

      const map = mapRef.current?.getMap();

      if (map && initialized.current) {
        // Use fitBounds for animated transitions after initial load
        // Padding is already set on the map via setPadding, so fitBounds respects it
        map.fitBounds(
          [
            [minLng, minLat],
            [maxLng, maxLat],
          ],
          {
            duration: 1000, // Animation duration in ms
          }
        );
        initialized.current = true;
        // Clear zoomToGeom after successful zoom
        clearZoomToGeom();
      } else {
        // For initial load, use the reducer to set viewport without animation
        // Use window dimensions as fallback if viewport container hasn't been measured yet
        const effectiveWidth =
          viewport.width > 0 ? viewport.width : window.innerWidth;
        const effectiveHeight =
          viewport.height > 0 ? viewport.height : window.innerHeight;

        if (effectiveWidth > 0 && effectiveHeight > 0) {
          // Update viewport with effective dimensions if container hasn't been measured
          if (viewport.width === 0 || viewport.height === 0) {
            viewportDispatch({
              type: "update",
              payload: { width: effectiveWidth, height: effectiveHeight },
            });
          }

          viewportDispatch({
            type: "zoom-to-bounds",
            payload: { geom: zoomToGeom, animate: false },
          });
          initialized.current = true;
          // Clear zoomToGeom after successful zoom
          clearZoomToGeom();
        }
      }
    }
  }, [
    zoomToGeom,
    mapRef,
    clearZoomToGeom,
    viewport.width,
    viewport.height,
    isMapResizeComplete,
  ]);

  useEffect(() => {
    // searching for a farm postcode - always active
    if (postcodeLocation) {
      viewportDispatch({
        type: "update",
        payload: {
          latitude: postcodeLocation.latitude,
          longitude: postcodeLocation.longitude,
          zoom: 14,
        },
      });
    }
  }, [postcodeLocation]);

  useEffect(() => {
    // searching for a farm
    if (!farmid && navigatorLocation) {
      // only when there are no farms created, otherwise using only postcode based location
      viewportDispatch({
        type: "update",
        payload: {
          latitude: navigatorLocation.latitude,
          longitude: navigatorLocation.longitude,
          zoom: 10,
        },
      });
    }
  }, [navigatorLocation, farmid]);

  const handleMapHover = useCallback(
    (e: MapLayerMouseEvent) => {
      const currentMap = mapRef.current?.getMap();
      if (
        currentMap &&
        e.features &&
        e.features.length > 0 &&
        !isDraggingAnimalMarker
      ) {
        const id = e.features[0].properties?.id as string | undefined;
        if (!id) {
          return;
        }
        if (
          fieldsWithNoTiles.includes(id) ||
          arableFieldsWithNoTiles.includes(id)
        ) {
          return;
        }

        setHoveredFieldId(id);

        const canvas = currentMap.getCanvas();
        canvas.style.cursor = "pointer";
        // Force a repaint to ensure fresh pixel data
        currentMap.triggerRepaint();

        // Use requestAnimationFrame to read pixels after repaint
        requestAnimationFrame(() => {
          const gl =
            canvas.getContext("webgl", { preserveDrawingBuffer: true }) ||
            canvas.getContext("webgl2", { preserveDrawingBuffer: true });
          if (gl) {
            const { point } = e;
            const x = point.x;
            const y = point.y;
            const data = new Uint8Array(4);

            // this adds support for screens with high pixel density
            const devicePixelRatio = window.devicePixelRatio || 1;
            const canvasX = (x - canvas.offsetLeft) * devicePixelRatio;
            const canvasY =
              canvas.height - (y - canvas.offsetTop) * devicePixelRatio;
            gl.readPixels(
              canvasX,
              canvasY,
              1,
              1,
              gl.RGBA,
              gl.UNSIGNED_BYTE,
              data
            );

            const [r, g, b] = data;

            const dm = colorToDM({ r: r, g: g, b: b });
            setMapColorDM(dm.toString());
            setTooltipVisible(!tooltipOverMarker);
          }
        });
      } else {
        setTooltipVisible(false);
        if (currentMap?.getCanvas()?.style) {
          currentMap.getCanvas().style.cursor = "grab";
        }
      }
    },
    [
      mapRef,
      fieldsWithNoTiles,
      isDraggingAnimalMarker,
      tooltipOverMarker,
      arableFieldsWithNoTiles,
    ]
  );

  const handleMapClick = useCallback(
    (e: MapLayerMouseEvent) => {
      // Don't handle map clicks in field edit mode - Terra Draw handles those
      if (isFieldEditMode) {
        return;
      }
      // Don't handle map clicks when clicks are disabled or for corporate users
      if (!disableMapClick && !isCorporateUser) {
        // when we are not in edit mode, clicking on a field polygon selects a feature
        if (e.features && e.features[0]) {
          // Prevent double-firing on touch devices
          if (e.originalEvent && e.originalEvent.type === "touchend") {
            e.originalEvent.preventDefault();
          }
          // On mobile, use push to maintain history for back navigation
          // On web, use replace since the drawer is always visible
          const fieldId = e.features[0].properties?.id;
          mixpanel.track(AnalyticsEvents.MAP_FIELD_TAPPED, {
            fieldGuid: fieldId,
          });
          const fieldPath = `/field/${fieldId}?from=map`;
          if (isMobile) {
            navigate(fieldPath);
          } else {
            navigate(fieldPath, { replace: true });
          }
          openDrawer();
        } else {
          if (isMobile) {
            navigate(`/farm/${farmid}`);
          } else {
            navigate(`/farm/${farmid}`, { replace: true });
          }
        }
      }
    },
    [
      isFieldEditMode,
      disableMapClick,
      farmid,
      navigate,
      openDrawer,
      isMobile,
      isCorporateUser,
    ]
  );

  // Handle viewport changes from the map
  const handleMove = useCallback(
    (e: ViewStateChangeEvent) => {
      const { viewState } = e;

      const newViewport: Partial<ViewportState> = {
        latitude: viewState.latitude,
        longitude: viewState.longitude,
        zoom: viewState.zoom,
        bearing: viewState.bearing,
        pitch: viewState.pitch,
      };

      // doing sync update only when map is initialized and no other sync update is going on
      if (isSyncing.current || !initialized.current || !mapRef.current) {
        viewportDispatch({ type: "update", payload: newViewport });
      } else {
        isSyncing.current = true;
        flushSync(() =>
          viewportDispatch({ type: "update", payload: newViewport })
        );
        isSyncing.current = false;
      }
    },
    [mapRef]
  );

  const handleTransformRequest = useCallback((url: string) => {
    // for our own tiles we need to add auth header, for tiles on mapbox we dont
    if (url?.includes(`${import.meta.env.VITE_API_ENDPOINT}`)) {
      return {
        url: url,
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("jwt")}`,
          "Content-Type": "application/json",
        },
      };
    } else {
      return {
        url: url,
      };
    }
  }, []);

  // Show map when: user is logged in, not a corporate user, route config says show map, and no modals are active
  // Corporate users should never see the map as they don't have farms
  const shouldShowMap =
    loggedIn &&
    !isCorporateUser &&
    showMap &&
    !questionnaireStarted &&
    !quickAssessmentStarted;

  // Keep map mounted when logged in to avoid reload delays when navigating between routes
  // Only fully unmount when not logged in
  if (!loggedIn) {
    return null;
  }

  // Mapbox satellite streets style (Mapbox GL renderer)
  const mapStyle = "mapbox://styles/mapbox/satellite-streets-v12";

  return (
    <FieldMap
      ref={mapContainer}
      data-map-ready={(isMobile || tilesReady) && isMapResizeComplete}
      style={{
        visibility: shouldShowMap ? "visible" : "hidden",
        pointerEvents: shouldShowMap ? "auto" : "none",
      }}
    >
      <Map
        key={mapKey}
        ref={mapRef}
        mapboxAccessToken={import.meta.env.VITE_MAPBOX_ACCESS_TOKEN}
        longitude={viewport.longitude}
        latitude={viewport.latitude}
        zoom={viewport.zoom}
        minZoom={1}
        bearing={viewport.bearing}
        pitch={viewport.pitch}
        hash={false}
        projection="mercator"
        mapStyle={mapStyle}
        onLoad={handleMapLoad}
        onMove={handleMove}
        interactiveLayerIds={isFieldEditMode ? undefined : interactiveLayerIds}
        onClick={isFieldEditMode ? undefined : handleMapClick}
        onMouseMove={isFieldEditMode ? undefined : handleMapHover}
        dragRotate={false}
        maxZoom={maxZoom}
        maxPitch={0}
        pitchWithRotate={false}
        transformRequest={handleTransformRequest}
        attributionControl={false}
        // Tile caching options to prevent white tiles when zooming rapidly
        maxTileCacheSize={300}
        refreshExpiredTiles={false}
      >
        <MapControls />
        <MapCursorTooltip
          fieldId={hoveredFieldId}
          dm={mapColorDM}
          visible={tooltipVisible}
        />
        {isFieldEditMode && <TerraDrawMapEditor />}
        <FieldGeoJSONLayers
          setTooltipOverMarker={setTooltipOverMarker}
          fieldId={selectedFieldId ?? undefined}
          fillLayers={isMobile}
          mapRef={mapRef}
          editedFieldId={editedFieldId ?? undefined}
          viewportZoom={viewport.zoom}
          tilesReady={tilesReady}
        />
        <ArableFieldGeoJSONLayers
          setTooltipOverMarker={setTooltipOverMarker}
          fieldId={selectedFieldId ?? undefined}
          fillLayers={isMobile}
          mapRef={mapRef}
          editedFieldId={editedFieldId ?? undefined}
          viewportZoom={viewport.zoom}
          tilesReady={tilesReady}
        />
        {/* Only render tile layers on desktop when zoomed in enough */}
        {!isMobile && (
          <FieldTileLayers
            editedFieldId={editedFieldId ?? undefined}
            zoom={viewport.zoom}
            onTilesReadyChange={handleTilesReadyChange}
          />
        )}
      </Map>
    </FieldMap>
  );
};

// Custom map controls component with zoom buttons and attribution in a single container
function MapControls() {
  const { current: map } = useMap();
  const isMobile = useIsMobile();
  const [showAttribution, setShowAttribution] = useState(false);

  const handleZoomIn = useCallback(() => {
    map?.zoomIn();
  }, [map]);

  const handleZoomOut = useCallback(() => {
    map?.zoomOut();
  }, [map]);

  const toggleAttribution = useCallback(() => {
    setShowAttribution((prev) => !prev);
  }, []);

  return (
    <MapControlsContainer $isMobile={isMobile}>
      <ControlButton onClick={handleZoomIn} size="small">
        <AddIcon />
      </ControlButton>
      <ControlDivider />
      <ControlButton onClick={handleZoomOut} size="small">
        <RemoveIcon />
      </ControlButton>
      <ControlDivider />
      <Tooltip
        title={
          showAttribution
            ? "© OpenStreetMap contributors © Mapbox"
            : "Map attribution"
        }
        placement="left"
        open={showAttribution}
        onClose={() => setShowAttribution(false)}
        arrow
      >
        <ControlButton onClick={toggleAttribution} size="small">
          <InfoOutlinedIcon fontSize="small" />
        </ControlButton>
      </Tooltip>
    </MapControlsContainer>
  );
}

const MapControlsContainer = styled("div", {
  shouldForwardProp: (prop) => prop !== "$isMobile",
})<{ $isMobile: boolean }>(({ theme, $isMobile }) => ({
  position: "fixed",
  bottom: $isMobile ? "calc(30vh + 16px)" : theme.spacing(3),
  right: theme.spacing(2),
  display: "flex",
  flexDirection: "column",
  backgroundColor: "white",
  borderRadius: theme.spacing(0.5),
  boxShadow: "0 0 0 2px rgba(0,0,0,.1)",
  zIndex: 1,
}));

const ControlButton = styled(IconButton)({
  borderRadius: 0,
  width: 29,
  height: 29,
  padding: 5,
  color: "#333",
  // Only apply hover effect on devices that support hover (not touch)
  "@media (hover: hover)": {
    "&:hover": {
      backgroundColor: "#f0f0f0",
    },
  },
  // Active state for touch feedback
  "&:active": {
    backgroundColor: "#e0e0e0",
  },
});

const ControlDivider = styled("div")({
  height: 1,
  backgroundColor: "#ddd",
});

const FieldMap = styled("div")(({ theme }) => ({
  width: "100%",
  // On mobile, map is rendered as a sibling with `display: contents` parent,
  // so we need 100vh. On desktop, map is in a flex container with defined height,
  // so 100% works.
  // On native Capacitor platforms (iOS/Android), always use 100vh regardless of
  // viewport width - this ensures iPad gets the same treatment as iPhone.
  height: "100vh",
  ...(!IS_NATIVE_PLATFORM && {
    [theme.breakpoints.up("sm")]: {
      height: "100%",
    },
  }),
  position: "relative",
  zIndex: theme.zIndex.drawer - 1,
}));
